import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus } from "lucide-react";
import { ADMIN } from "@/constants/testIds";
import { PROMO_CATEGORIES } from "@/constants/categories";
import { DatePicker } from "@/components/DatePicker";
import { TerminalSelect } from "@/components/admin/promos/TerminalSelect";

const CORNER_TAG_PRESETS = ["OFFER", "NEW", "TRAVEL EXCLUSIVE", "FREE GIFTS", "LOVE EARTH"];

// Small HEX + swatch row used by the promo form fields.
function HexColorRow({ label, value, def, onChange, tid }) {
  const v = value || def;
  return (
    <div className="flex items-center gap-2">
      <Label className="flex-1 text-xs">{label}</Label>
      <Input
        type="color"
        value={v}
        onChange={(e) => onChange(e.target.value)}
        className="w-12 h-9 p-1"
        data-testid={tid}
      />
      <Input
        type="text"
        value={v}
        onChange={(e) => {
          const nv = e.target.value;
          if (/^#[0-9a-fA-F]{0,6}$/.test(nv)) onChange(nv);
        }}
        placeholder={def}
        className="w-24 h-9 font-mono text-xs"
        maxLength={7}
        data-testid={`${tid}-hex`}
      />
    </div>
  );
}

export default function PromoForm({
  promoLoc,
  setPromoLoc,
  promoForm,
  setPromoForm,
  promoImgRef,
  onAddPromo,
  saving,
}) {
  const set = (patch) => setPromoForm({ ...promoForm, ...patch });
  return (
    <div className="grid gap-2">
      <Label>Location</Label>
      <Select value={promoLoc} onValueChange={setPromoLoc}>
        <SelectTrigger data-testid={ADMIN.promoLocSelect}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="departures">Departures</SelectItem>
          <SelectItem value="arrivals">Arrivals</SelectItem>
        </SelectContent>
      </Select>

      <label
        className="flex items-center gap-2 rounded-md border p-2 cursor-pointer select-none"
        data-testid="admin-promo-imageonly-wrap"
      >
        <input
          type="checkbox"
          checked={promoForm.imageOnly}
          onChange={(e) => set({ imageOnly: e.target.checked })}
          className="h-4 w-4"
          data-testid="admin-promo-imageonly-checkbox"
        />
        <span className="text-sm font-semibold">IMAGE ONLY</span>
        <span className="text-xs text-muted-foreground">
          — image serves as the card; no details shown
        </span>
      </label>
      {promoForm.imageOnly && (
        <>
          <p
            className="text-xs text-muted-foreground italic"
            data-testid="admin-promo-imageonly-hint"
          >
            Image-only card: pick the location + category above and upload an image below (required). No text is shown on the card.
          </p>
          <Label className="text-xs">Image Name (used in analytics reports)</Label>
          <Input
            placeholder="e.g. Gold Special Offers May"
            value={promoForm.imageName || ""}
            onChange={(e) => set({ imageName: e.target.value })}
            maxLength={80}
            data-testid="admin-promo-image-name"
          />
        </>
      )}

      <Select value={promoForm.category} onValueChange={(v) => set({ category: v })}>
        <SelectTrigger data-testid={ADMIN.promoCat}>
          <SelectValue placeholder="Select category" />
        </SelectTrigger>
        <SelectContent>
          {PROMO_CATEGORIES.map((c) => (
            <SelectItem key={c} value={c}>
              {c}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {!promoForm.imageOnly && (
        <>
          <Input
            placeholder="Product name (e.g. GUCCI)"
            value={promoForm.name}
            onChange={(e) => set({ name: e.target.value })}
            data-testid={ADMIN.promoName}
          />
          <Input
            placeholder="Description (optional)"
            value={promoForm.description}
            onChange={(e) => set({ description: e.target.value })}
            data-testid={ADMIN.promoDesc}
          />
          <Input
            placeholder="Promo header (red ribbon e.g. Summer Sale)"
            value={promoForm.header}
            onChange={(e) => set({ header: e.target.value })}
            data-testid={ADMIN.promoHeader}
          />
          <HexColorRow
            label="Ribbon color"
            value={promoForm.ribbonColor}
            def="#e63946"
            onChange={(v) => set({ ribbonColor: v })}
            tid="admin-promo-ribbon-color"
          />
          <HexColorRow
            label="Ribbon Text Color"
            value={promoForm.ribbonTextColor}
            def="#ffffff"
            onChange={(v) => set({ ribbonTextColor: v })}
            tid="admin-promo-ribbon-text-color"
          />
          <div className="rounded-md border p-2 space-y-2">
            <Label className="text-xs font-semibold">Corner Tag (top-right of product image)</Label>
            <Select
              value={promoForm.cornerTagPreset}
              onValueChange={(v) => {
                if (v === "none") set({ cornerTagPreset: "none", cornerTag: "" });
                else if (v === "custom") set({ cornerTagPreset: "custom" });
                else set({ cornerTagPreset: v, cornerTag: v });
              }}
            >
              <SelectTrigger data-testid="admin-promo-corner-preset">
                <SelectValue placeholder="No corner tag" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No corner tag</SelectItem>
                {CORNER_TAG_PRESETS.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
                <SelectItem value="custom">Custom…</SelectItem>
              </SelectContent>
            </Select>
            {promoForm.cornerTagPreset === "custom" && (
              <Input
                placeholder="Custom tag text (e.g. LAST CHANCE)"
                value={promoForm.cornerTag}
                onChange={(e) => set({ cornerTag: e.target.value })}
                data-testid="admin-promo-corner-text"
              />
            )}
            {promoForm.cornerTagPreset !== "none" && (
              <>
                <HexColorRow
                  label="Tag background"
                  value={promoForm.cornerTagColor}
                  def="#111827"
                  onChange={(v) => set({ cornerTagColor: v })}
                  tid="admin-promo-corner-color"
                />
                <HexColorRow
                  label="Tag text color"
                  value={promoForm.cornerTagTextColor}
                  def="#ffffff"
                  onChange={(v) => set({ cornerTagTextColor: v })}
                  tid="admin-promo-corner-text-color"
                />
              </>
            )}
          </div>
          <Input
            type="number"
            step="0.01"
            placeholder="Price (AED)"
            value={promoForm.priceAED}
            onChange={(e) => set({ priceAED: e.target.value })}
            data-testid={ADMIN.promoPrice}
          />
          <Input
            type="number"
            step="0.01"
            placeholder="WAS price (AED, optional, shown crossed out)"
            value={promoForm.wasPrice}
            onChange={(e) => set({ wasPrice: e.target.value })}
            data-testid="admin-promo-was-price"
          />
          <Label className="text-xs">Validity (Valid until)</Label>
          <DatePicker
            value={promoForm.validUntil}
            onChange={(v) => set({ validUntil: v })}
            placeholder="Pick a valid-until date"
            testId="admin-promo-validuntil"
          />
          <Label className="text-xs">Terminal / Shop name (optional)</Label>
          <TerminalSelect
            value={promoForm.terminal}
            specific={promoForm.terminalSpecific}
            onChange={(terminal, terminalSpecific) => set({ terminal, terminalSpecific })}
            testId="admin-promo-terminal"
          />
        </>
      )}
      <Input
        type="file"
        accept="image/*"
        ref={promoImgRef}
        data-testid={ADMIN.promoImage}
      />
      <Button onClick={onAddPromo} disabled={saving} data-testid={ADMIN.addPromoItemBtn}>
        <Plus size={14} /> Add Promo
      </Button>
    </div>
  );
}
